import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowDown } from 'lucide-react'
import { revealDelay } from '@/lib/motion'
import { CtaLink } from '@/components/generals/cta-link'
import { HeroTitle } from '@/components/generals/hero-title'
import { Mark } from '@/components/generals/mark'

const ANNOTATIONS = [
    { label: 'Strategy / 001', className: 'left-0 top-[6%] lg:-left-10', line: 'left-full w-6 md:w-10', delay: 1000 },
    { label: 'Creative / 002', className: 'right-0 top-[44%] lg:-right-6', line: 'right-full w-6 md:w-12', delay: 1100 },
    { label: 'Performance / 003', className: 'bottom-[14%] left-[4%] lg:-left-14', line: 'left-full w-6 md:w-10', delay: 1200 }
]

const HomeHero: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' data-nav-theme='light' className='relative overflow-hidden bg-cream text-charcoal'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 mx-auto hidden max-w-[1440px] px-10 lg:block'>
                <div className='grid h-full grid-cols-4'>
                    {[0, 1, 2, 3].map((column) => (
                        <span key={column} className='reveal-fade border-l border-charcoal/[0.06] last:border-r' style={revealDelay(column * 80)} />
                    ))}
                </div>
            </div>

            <div className='relative mx-auto grid max-w-[1440px] gap-14 px-5 pb-8 pt-32 md:px-10 md:pt-40 lg:min-h-[min(100svh,60rem)] lg:grid-cols-12 lg:gap-8 lg:pb-10'>
                <div className='flex flex-col lg:col-span-7 lg:justify-center lg:pr-8'>
                    <p className='reveal-fade flex items-center gap-3 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-charcoal/80'>
                        <span aria-hidden='true' className='size-2 bg-coral' />
                        Creative marketing agency
                        <span aria-hidden='true' className='h-px w-10 bg-charcoal/40' />
                    </p>

                    <HeroTitle
                        id='hero-title'
                        className='mt-7 type-display md:mt-8'
                        delay={150}
                        step={120}
                        lines={[
                            'We make brands',
                            <><Mark>impossible</Mark> to ignore.</>
                        ]}
                    />

                    <p className='reveal-fade mt-7 max-w-[30rem] text-[17px] leading-relaxed text-charcoal/85 md:mt-8 md:text-lg' style={revealDelay(480)}>
                        We build bold brands, creative campaigns, and marketing strategies that turn attention into measurable business growth.
                    </p>

                    <div className='reveal-fade mt-9 flex flex-wrap items-center gap-x-8 gap-y-5' style={revealDelay(580)}>
                        <CtaLink href='/contact'>Start a project</CtaLink>
                        <CtaLink href='/work' variant='text' icon='up-right'>Explore our work</CtaLink>
                    </div>
                </div>

                <div className='relative mx-auto w-full max-w-[520px] lg:col-span-5 lg:mx-0 lg:max-w-none lg:self-center'>
                    <div className='relative aspect-square w-full'>
                        <div aria-hidden='true' className='absolute right-0 top-[8%] h-[70%] w-[64%]' data-parallax='6'>
                            <div className='reveal-clip size-full bg-coral' style={revealDelay(250)} />
                        </div>
                        <div aria-hidden='true' className='reveal-clip absolute bottom-[4%] left-0 h-[38%] w-[44%] bg-stone' style={revealDelay(420)} />

                        <div className='reveal-clip absolute inset-x-[9%] inset-y-[5%] overflow-hidden bg-stone' style={revealDelay(380)}>
                            <Image
                                src='/images/hero/hero-campaign.png'
                                alt='Brand packaging boxes and a glossy orange vase arranged on sunlit stone steps'
                                fill
                                priority
                                sizes='(min-width: 1024px) 36vw, 90vw'
                                className='object-cover object-center'
                            />

                            <span className='absolute left-3 top-3 bg-charcoal px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream'>
                                Campaign / Concept 26
                            </span>
                        </div>

                        <div aria-hidden='true' className='reveal-fade absolute -bottom-2 right-[6%] h-14 w-28 rounded-t-full bg-charcoal md:h-16 md:w-32' style={revealDelay(900)} />
                        <div aria-hidden='true' className='reveal-fade absolute left-[4%] top-0 size-10 rounded-full border border-charcoal md:size-12' style={revealDelay(950)} />

                        {ANNOTATIONS.map((note) => (
                            <span
                                key={note.label}
                                className={`reveal-fade absolute hidden items-center gap-2 bg-cream px-2.5 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-charcoal shadow-[0_1px_0_rgba(37,37,34,0.15)] sm:flex ${note.className}`}
                                style={revealDelay(note.delay)}
                            >
                                <span aria-hidden='true' className='size-1.5 bg-coral' />
                                {note.label}
                                <span aria-hidden='true' className={`absolute top-1/2 h-px bg-charcoal ${note.line}`} />
                            </span>
                        ))}
                    </div>
                </div>

                <div className='relative flex flex-col gap-4 pt-5 text-charcoal/85 sm:flex-row sm:items-center sm:justify-between lg:col-span-12 lg:self-end'>
                    <span aria-hidden='true' className='reveal-rule absolute inset-x-0 top-0 h-px bg-charcoal/20' style={revealDelay(700)} />
                    <p className='reveal-fade font-mono text-[11px] font-medium uppercase tracking-[0.2em]' style={revealDelay(850)}>
                        Independent thinking. Creative execution. Measurable growth.
                    </p>
                    <a href='#clients' className='reveal-fade group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors hover:text-charcoal' style={revealDelay(950)}>
                        Scroll
                        <ArrowDown aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-y-0.5' />
                    </a>
                </div>
            </div>
        </section>
    )
}

export default HomeHero
